<script setup lang="ts">
/*
 * Info sheet: a bottom sheet opened from a tile's info button via
 * `useUiStore().quickViewId`. Mounted once in App.vue; fetches the product
 * and its offers on open, with a sequence guard so a slow response for a
 * previous tile can't overwrite the current one. The price is the cheapest
 * live offer, so what is shown is what "Add to cart" adds. Route changes
 * close it (App.vue calls `closeAll`).
 */
import { Heart, X } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouterLink } from 'vue-router';
import { api } from '../../api/client';
import { useAddToCart } from '../../composables/useAddToCart';
import { useFavoriteToggle } from '../../composables/useFavoriteToggle';
import { useModalFocus } from '../../composables/useModalFocus';
import { useUiStore } from '../../stores/ui';
import { isPurchasable, productArt, type ProductDetail, type ProductOffer } from '../../types/product';
import { knownPlatform, regionChip, releaseChip } from '../product/productFacts';
import PriceTag from '../PriceTag.vue';

const ui = useUiStore();
const { t } = useI18n();
const { add, buyNow } = useAddToCart();
const favorite = useFavoriteToggle();

const open = computed(() => ui.quickViewId !== null);
const product = ref<ProductDetail | null>(null);
const offers = ref<ProductOffer[]>([]);
const loading = ref(false);
const failed = ref(false);

let loadSeq = 0;
async function load(id: string | null) {
  const seq = ++loadSeq;
  product.value = null;
  offers.value = [];
  failed.value = false;
  if (!id) return;
  loading.value = true;
  try {
    const [detail, list] = await Promise.all([
      api.get<ProductDetail>(`/products/${id}`),
      api.get<{ items: ProductOffer[] }>(`/products/${id}/offers`),
    ]);
    if (seq !== loadSeq) return;
    product.value = detail.data;
    offers.value = list.data.items;
  } catch {
    if (seq === loadSeq) failed.value = true;
  } finally {
    if (seq === loadSeq) loading.value = false;
  }
}
watch(() => ui.quickViewId, load, { immediate: true });

const liveOffers = computed(() => offers.value.filter((o) => o.available && o.qty > 0));
const bestOffer = computed(() => liveOffers.value.reduce<ProductOffer | null>((best, o) => (!best || o.ourPriceCents < best.ourPriceCents ? o : best), null));
const buyable = computed(() => product.value !== null && bestOffer.value !== null && isPurchasable(product.value));
const art = computed(() => (product.value ? productArt(product.value) : null));
const chips = computed(() => {
  const p = product.value;
  if (!p) return [];
  return [knownPlatform(p), regionChip(p, (count) => t('product.chips.countries', { count })), releaseChip(p)].filter((c): c is string => Boolean(c));
});

const panel = ref<HTMLElement | null>(null);
const closeButton = ref<HTMLElement | null>(null);
const header = ref<HTMLElement | null>(null);
useModalFocus(panel, open, ui.closeQuickView, { initial: closeButton });

function addToCart() {
  const offer = bestOffer.value;
  if (!product.value || !offer) return;
  add(product.value, { from: header.value, offer: { id: offer.id, merchantName: offer.merchantName, ourPriceCents: offer.ourPriceCents, qty: offer.qty } });
}
</script>

<template>
  <div v-if="open" class="fixed inset-0 z-[60] flex items-end justify-center">
    <div class="absolute inset-0 bg-black/70" aria-hidden="true" @click="ui.closeQuickView" />
    <div
      ref="panel"
      role="dialog"
      aria-modal="true"
      :aria-label="product?.name ?? $t('catalog.quickView.label')"
      tabindex="-1"
      class="animate-rise relative max-h-[85vh] w-full max-w-[720px] overflow-y-auto rounded-t-xl border border-b-0 border-border bg-bg-elev outline-none"
    >
      <button ref="closeButton" type="button" class="btn-icon absolute right-3 top-3 z-10" :aria-label="$t('common.actions.close')" @click="ui.closeQuickView">
        <X class="h-5 w-5" aria-hidden="true" />
      </button>

      <div ref="header" class="relative aspect-video bg-bg-elev-2">
        <div v-if="loading" class="skeleton absolute inset-0 !rounded-none" />
        <img v-else-if="art" :src="art" :alt="product?.name" class="absolute inset-0 h-full w-full object-cover" />
        <span v-else class="absolute inset-0 grid place-items-center"><img src="/favicon.svg" alt="" class="h-14 w-14 opacity-40" /></span>
      </div>

      <div v-if="failed" class="flex flex-col items-start gap-4 p-6">
        <p class="font-heading text-[20px] font-extrabold text-text-base">{{ $t('common.state.error') }}</p>
        <p class="text-[14px] text-text-dim">{{ $t('catalog.errorBody') }}</p>
        <button type="button" class="btn-secondary" @click="load(ui.quickViewId)">{{ $t('common.actions.retry') }}</button>
      </div>

      <div v-else-if="loading || !product" class="flex flex-col gap-4 p-6" aria-busy="true">
        <div class="skeleton h-7 w-4/5" />
        <div class="skeleton h-9 w-1/3" />
        <div class="skeleton h-12 w-full" />
      </div>

      <div v-else class="flex flex-col gap-4 p-5 md:p-6">
        <h2 class="font-heading text-[22px] font-extrabold leading-tight text-text-base">{{ product.name }}</h2>
        <div v-if="chips.length" class="flex flex-wrap gap-2">
          <span v-for="c in chips" :key="c" class="chip">{{ c }}</span>
        </div>

        <PriceTag v-if="bestOffer" :cents="bestOffer.ourPriceCents" size="lg" :from="liveOffers.length > 1" />
        <p v-else class="text-[15px] font-semibold text-text-dim">{{ $t('product.card.delivery.outOfStock') }}</p>

        <div class="flex flex-wrap items-center gap-2">
          <button type="button" class="btn-primary flex-1 sm:flex-none" :disabled="!buyable" @click="addToCart">{{ $t('common.actions.addToCart') }}</button>
          <button type="button" class="btn-secondary" :disabled="!buyable" @click="buyNow(product)">{{ $t('common.actions.buyNow') }}</button>
          <RouterLink :to="`/catalog/${product.id}`" class="btn-secondary">{{ $t('catalog.quickView.viewFullPage') }}</RouterLink>
          <button
            type="button"
            class="btn-icon ml-auto"
            :class="favorite.isFavorite(product.id) ? '!text-heart' : ''"
            :aria-label="favorite.isFavorite(product.id) ? $t('product.wishlistAriaLabel.remove') : $t('product.wishlistAriaLabel.add')"
            :aria-pressed="favorite.isFavorite(product.id)"
            @click="favorite.toggle(product)"
          >
            <Heart class="h-5 w-5" :fill="favorite.isFavorite(product.id) ? 'currentColor' : 'none'" aria-hidden="true" />
          </button>
        </div>

        <p v-if="product.description" class="line-clamp-3 whitespace-pre-line text-[14px] leading-6 text-text-dim">{{ product.description }}</p>
      </div>
    </div>
  </div>
</template>
